import React, { useState } from "react";
import { useAuth } from "react-oidc-context";
import axios from "axios";
import config from "./config.json";
import Radio from "@mui/material/Radio";
import Stack from "@mui/material/Stack";
import Slider from "@mui/material/Slider";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import RadioGroup from "@mui/material/RadioGroup";
import Typography from "@mui/material/Typography";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import InfoIcon from "@mui/icons-material/Info";
import { Dialog, DialogTitle, DialogContent, DialogActions, Box, Drawer } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import ChatPrompt from "./ChatPrompt";
import { useQuery } from "@tanstack/react-query"; 

export interface Episode {
  code: string;
  name: string;
  guest?: string;
  topics?: string[];
  referenced_named_entities?: string;
  mini_series?: string;
  description?: string;
}

export function filterEpisodes(options: Episode[], inputValue: string): Episode[] {
  if (!inputValue) return options;
  const terms = inputValue.toLowerCase().split(/\s+/).filter(Boolean);
  return options.filter((ep) => {
    const searchable = [
      ep.name,
      ep.guest || '',
      (ep.topics || []).join(' '),
      ep.referenced_named_entities || '',
      ep.mini_series || '',
      ep.description || '',
    ].join(' ').toLowerCase();
    return terms.every((term) => searchable.includes(term));
  });
}

const valuetext = (value: number) => `${value}`;

function ChatPage() {
  const auth = useAuth();
  const isAdmin = (auth.user?.profile?.["cognito:groups"] as string[] | undefined)?.includes('admin') ?? false;
  
  // Unified drawer state for ALL screen sizes
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);
  
  const defaultScoreThreshold = 0.1;
  const defaultUseOnlyTranscriptsFlag = false;
  const defaultHistoryRetention = 2;
  const defaultListTextChunks = false;
  const [scoreThreshold, setScoreThreshold] = useState(defaultScoreThreshold);
  const [useOnlyTranscriptsFlag, setUseOnlyTranscriptsFlag] = useState(defaultUseOnlyTranscriptsFlag);
  const [historyRetention, setHistoryRetention] = useState(defaultHistoryRetention);
  const [listTextChunksFlag, setListTextChunksFlag] = useState(defaultListTextChunks);
  const [selectedEpisodes, setSelectedEpisodes] = useState<Episode[]>([]);

  const [openFirstDialog, setOpenFirstDialog] = useState(false);
  const [openSecondDialog, setOpenSecondDialog] = useState(false);
  const [openPdfDialog, setOpenPdfDialog] = useState(false);
  const [advancedSettingsOpen, setAdvancedSettingsOpen] = useState(false);

  const handleOpenAdvancedSettings = () => setAdvancedSettingsOpen(true);
  const handleCloseAdvancedSettings = () => setAdvancedSettingsOpen(false);
  const userApiKey = auth.user?.profile?.['custom:user_api_key'] as string;

  const fetchEpisodes = async () => {
    const episodesUrl = `${config.api.chatbot.url}/episodes`;
    const response = await axios.get<Episode[]>(episodesUrl, {
      headers: {
        'Authorization': auth.user?.id_token,
        'X-Api-Key': userApiKey,
      },
      timeout: config.timeouts.chatApi,
    });
    return response.data;
  };

  const { data: episodes } = useQuery({
    queryKey: ['episodes', userApiKey],
    queryFn: fetchEpisodes,
    enabled: !!auth.user?.id_token && !!userApiKey,
  });

  const resetSettings = () => {
    setScoreThreshold(defaultScoreThreshold);
    setUseOnlyTranscriptsFlag(defaultUseOnlyTranscriptsFlag);
    setHistoryRetention(defaultHistoryRetention);
    setListTextChunksFlag(defaultListTextChunks);
  };

  const handleScoreThresholdChange = (event: Event, newValue: number | number[]) => setScoreThreshold(newValue as number);
  const handleUseOnlyTranscriptsFlagChange = (event: React.ChangeEvent<HTMLInputElement>) => setUseOnlyTranscriptsFlag(event.target.value === "1");
  const handleHistoryRetentionChange = (event: Event, newValue: number | number[]) => setHistoryRetention(newValue as number);
  const handleListTextChunksFlagChange = (event: React.ChangeEvent<HTMLInputElement>) => setListTextChunksFlag(event.target.value === "1");

  const handleOpenFirstDialog = () => setOpenFirstDialog(true);
  const handleCloseFirstDialog = () => setOpenFirstDialog(false);
  const handleOpenSecondDialog = () => setOpenSecondDialog(true);
  const handleCloseSecondDialog = () => setOpenSecondDialog(false);
  const handleOpenPdfDialog = () => setOpenPdfDialog(true);
  const handleClosePdfDialog = () => setOpenPdfDialog(false);

  const tooltipText = (text: string) => (
    <span style={{ fontSize: "0.9rem", display: "block", maxWidth: "200px", whiteSpace: "pre-line" }}>
      {text}
    </span>
  );

  // Modernized Side Panel
  const sidePanel = (
    <Stack
      direction="column"
      spacing={3}
      sx={{
        padding: "24px",
        width: { xs: "85vw", sm: "360px" },
        backgroundColor: "background.paper",
        height: "100%",
        overflowY: "auto",
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: "-0.5px" }}>
          Tools & Settings
        </Typography>
        <IconButton onClick={toggleSidebar} size="small" sx={{ backgroundColor: "action.hover" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Box
        sx={{
          background: "linear-gradient(135deg, #ff9800 0%, #f44336 100%)",
          borderRadius: "12px",
          p: 2,
          color: "#fff",
          boxShadow: "0 4px 20px rgba(244, 67, 54, 0.3)",
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,0.03) 10px, rgba(255,255,255,0.03) 20px)",
            pointerEvents: "none",
          },
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              backgroundColor: "rgba(255,255,255,0.2)",
              borderRadius: "50%",
              width: 36,
              height: 36,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              fontSize: "1.1rem",
            }}
          >
            &#x1F6E1;
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, letterSpacing: "0.5px", textTransform: "uppercase", fontSize: "0.7rem", opacity: 0.9 }}>
              Restricted Access
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.4 }}>
              Admin-only panel. These controls are not visible to regular users.
            </Typography>
          </Box>
        </Stack>
      </Box>

      <Button
        onClick={handleOpenAdvancedSettings}
        variant="contained"
        color="primary"
        disableElevation
        fullWidth
        sx={{ textTransform: "none", borderRadius: "8px", py: 1 }}
      >
        Advanced Search Settings
      </Button>

      <Divider />

      <Box>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 600 }}>
          System Documentation
        </Typography>
        <Stack direction="row" spacing={1} sx={{ width: "100%" }}>
          <Button onClick={handleOpenFirstDialog} variant="outlined" size="small" fullWidth sx={{ textTransform: "none", borderRadius: "8px" }}>
            Diagram
          </Button>
          <Button onClick={handleOpenSecondDialog} variant="outlined" size="small" fullWidth sx={{ textTransform: "none", borderRadius: "8px" }}>
            Flowchart
          </Button>
          <Button onClick={handleOpenPdfDialog} variant="outlined" size="small" fullWidth sx={{ textTransform: "none", borderRadius: "8px" }}>
            Examples
          </Button>
        </Stack>
      </Box>

      {/* Keep the Dialogs exactly as they were, just rendering at the bottom of the panel */}
      <Dialog open={openFirstDialog} onClose={handleCloseFirstDialog} maxWidth="lg" fullWidth>
        <DialogTitle>
          Diagram
          <IconButton onClick={handleCloseFirstDialog} sx={{ position: "absolute", right: 8, top: 8 }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ display: "flex", justifyContent: "center", height: "70vh" }}>
          <img src="/llm_diagram.png" alt="Diagram" style={{ width: "auto", height: "100%", objectFit: "contain", borderRadius: '8px', padding: '8px', backgroundColor: 'white' }} />
        </DialogContent>
      </Dialog>

      <Dialog open={openSecondDialog} onClose={handleCloseSecondDialog} maxWidth="lg" fullWidth>
        <DialogTitle>
          Flowchart
          <IconButton onClick={handleCloseSecondDialog} sx={{ position: "absolute", right: 8, top: 8 }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ display: "flex", justifyContent: "center", height: "70vh" }}>
          <img src="/llm_flowchart.png" alt="Flowchart" style={{ width: "auto", height: "100%", objectFit: "contain", borderRadius: '8px', padding: '8px', backgroundColor: 'white' }} />
        </DialogContent>
      </Dialog>

      <Dialog open={openPdfDialog} onClose={handleClosePdfDialog} maxWidth="lg" fullWidth>
        <DialogTitle>
          Examples
          <IconButton onClick={handleClosePdfDialog} sx={{ position: "absolute", right: 8, top: 8 }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ display: "flex", justifyContent: "center", height: "70vh" }}>
          <object data="/examples.pdf" type="application/pdf" width="100%" height="100%">
            <p>Your browser does not support PDFs. <a href="/examples.pdf">Download the PDF</a>.</p>
          </object>
        </DialogContent>
      </Dialog>

      <Dialog open={advancedSettingsOpen} onClose={handleCloseAdvancedSettings} maxWidth="xs" fullWidth>
        {/* Same advanced settings dialog content as before */}
        <DialogTitle>
          Advanced Settings (Lab)
          <IconButton onClick={handleCloseAdvancedSettings} sx={{ position: "absolute", right: 8, top: 8, color: "text.secondary" }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3} sx={{ pt: 1 }}>
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Typography variant="body2" fontWeight={600}>Score Threshold</Typography>
                <Tooltip title={tooltipText(`1: Only perfect matches.\n0: All matches.\nIf too high, you might not get answers.`)} arrow><InfoIcon color="action" fontSize="small" /></Tooltip>
              </Stack>
              <Slider value={scoreThreshold} onChange={handleScoreThresholdChange} step={0.1} min={0.0} max={1.0} valueLabelDisplay="auto" />
            </Box>

            <Box>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Typography variant="body2" fontWeight={600}># Questions Retained</Typography>
                <Tooltip title={tooltipText(`Number of chat questions retained in history.`)} arrow><InfoIcon color="action" fontSize="small" /></Tooltip>
              </Stack>
              <Slider value={historyRetention} onChange={handleHistoryRetentionChange} step={1} min={0} max={10} valueLabelDisplay="auto" />
            </Box>
            
            <Divider />

            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="body2" fontWeight={600}>Only Transcripts?</Typography>
                <Tooltip title={tooltipText(`Yes: Only transcripts used.\nNo: All materials (references, news) used.`)} arrow><InfoIcon color="action" fontSize="small" /></Tooltip>
              </Stack>
              <RadioGroup value={useOnlyTranscriptsFlag ? "1" : "0"} onChange={handleUseOnlyTranscriptsFlagChange} row>
                <FormControlLabel value="1" control={<Radio size="small" />} label="Yes" />
                <FormControlLabel value="0" control={<Radio size="small" />} label="No" />
              </RadioGroup>
            </Stack>

            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="body2" fontWeight={600}>Show Metadata?</Typography>
                <Tooltip title={tooltipText(`Gains insights into information retrieved from the vector store.`)} arrow><InfoIcon color="action" fontSize="small" /></Tooltip>
              </Stack>
              <RadioGroup value={listTextChunksFlag ? "1" : "0"} onChange={handleListTextChunksFlagChange} row>
                <FormControlLabel value="1" control={<Radio size="small" />} label="Yes" />
                <FormControlLabel value="0" control={<Radio size="small" />} label="No" />
              </RadioGroup>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between", p: 2 }}>
          <Button onClick={resetSettings} color="error" sx={{ textTransform: 'none' }}>Reset Defaults</Button>
          <Button onClick={handleCloseAdvancedSettings} variant="contained" disableElevation sx={{ textTransform: 'none' }}>Done</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );

  return (
    <Box sx={{ display: "flex", height: "100%", width: "100%", position: "relative" }}>
      
      {/* Floating Toggle Button for Settings Drawer (admin only) */}
      {isAdmin && (
        <Box sx={{ position: "absolute", top: 16, left: 16, zIndex: 10 }}>
          <Tooltip title="Open Tools & Settings" placement="right">
            <IconButton
              onClick={toggleSidebar}
              sx={{
                backgroundColor: "background.paper",
                boxShadow: "0px 4px 12px rgba(0,0,0,0.1)",
                border: "1px solid",
                borderColor: "divider",
                "&:hover": { backgroundColor: "action.hover" }
              }}
            >
              <MenuOpenIcon color="action" />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* Slide-out Drawer (admin only) */}
      {isAdmin && (
        <Drawer
          anchor="left"
          open={sidebarOpen}
          onClose={toggleSidebar}
          sx={{
            "& .MuiDrawer-paper": {
              boxShadow: "4px 0px 24px rgba(0,0,0,0.1)",
              borderRight: "none"
            },
          }}
        >
          {sidePanel}
        </Drawer>
      )}

      {/* Main Chat Area - Now takes up the full screen width */}
      <Box sx={{ flexGrow: 1, width: "100%", height: "100%", overflow: "hidden" }}>
        <ChatPrompt
          scoreThreshold={scoreThreshold}
          useOnlyTranscriptsFlag={useOnlyTranscriptsFlag}
          setUseOnlyTranscriptsFlag={setUseOnlyTranscriptsFlag}
          historyRetention={historyRetention}
          setHistoryRetention={setHistoryRetention}
          listTextChunksFlag={listTextChunksFlag}
          episodes={episodes || []} 
          selectedEpisodes={selectedEpisodes}
          setSelectedEpisodes={setSelectedEpisodes}
        />
      </Box>
    </Box>
  );
}

export default ChatPage;